// SensorCompositeGraph.js - Fixed to handle BME688 hierarchical data and new hook API
import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LineChart } from '@mui/x-charts/LineChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { useSensorMeasurements } from '../hooks/useSensorMeasurements';
import { formatMeasurementName, getUnit, getLineColors } from '../utils/helpers';

// Component for BME688 with profile tabs
function BME688CompositeChart({ measurements }) {
  const [selectedProfile, setSelectedProfile] = useState(0);

  // Extract all unique profiles from measurements
  const profiles = useMemo(() => {
    if (!Array.isArray(measurements) || measurements.length === 0) return [];

    const firstMeasurement = measurements[0];
    if (!firstMeasurement?.measurement_data?.bme688) return [];

    let bme688Data = firstMeasurement.measurement_data.bme688;

    // Convert to array if it's an object with numeric keys
    if (!Array.isArray(bme688Data) && typeof bme688Data === 'object') {
      const keys = Object.keys(bme688Data);
      const isArrayLike = keys.length > 0 && keys.every(k => !isNaN(parseInt(k)));
      if (isArrayLike) {
        bme688Data = Object.values(bme688Data);
      } else {
        return [];
      }
    }

    if (!Array.isArray(bme688Data)) return [];

    return bme688Data.map((profile, idx) => ({
      index: idx,
      name: profile.meas_profile || `Profile ${idx + 1}`
    }));
  }, [measurements]);

  // Process data for selected profile
  const chartData = useMemo(() => {
    if (!Array.isArray(measurements) || measurements.length === 0) return [];

    return measurements
      .map(item => {
        let bme688Data = item?.measurement_data?.bme688;

        // Convert to array if needed
        if (!Array.isArray(bme688Data) && typeof bme688Data === 'object') {
          const keys = Object.keys(bme688Data);
          const isArrayLike = keys.length > 0 && keys.every(k => !isNaN(parseInt(k)));
          if (isArrayLike) {
            bme688Data = Object.values(bme688Data);
          }
        }

        if (!Array.isArray(bme688Data) || !bme688Data[selectedProfile]) return null;

        const profileData = bme688Data[selectedProfile];
        const timestamp = new Date(item.timestamp);

        return {
          timestamp: timestamp.getTime(),
          timestampFormatted: timestamp.toLocaleString(),
          ...profileData
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [measurements, selectedProfile]);

  const handleProfileChange = (event, newValue) => {
    setSelectedProfile(newValue);
  };

  if (profiles.length === 0) {
    return (
      <Alert severity="warning">
        No BME688 profile data found in measurements
      </Alert>
    );
  }

  if (chartData.length === 0) {
    return (
      <Alert severity="warning">
        No data available for selected profile
      </Alert>
    );
  }

  // Get measurement keys (excluding metadata)
  const sampleData = chartData[0];
  const measurementKeys = Object.keys(sampleData).filter(key =>
    key !== 'timestamp' &&
    key !== 'timestampFormatted' &&
    key !== 'meas_profile' &&
    key !== 'meas_id' &&
    key !== 'step' &&
    key !== 'status' &&
    typeof sampleData[key] === 'number'
  );

  const colors = getLineColors();

  // Prepare data for MUI X Charts
  const xAxisData = chartData.map(point => point.timestamp);
  const series = measurementKeys.map((key, index) => ({
    id: key,
    label: `${formatMeasurementName(key)} (${getUnit(key)})`,
    color: colors[index % colors.length],
    data: chartData.map(point => point[key]),
    connectNulls: true
  }));

  // Calculate statistics
  const calculateStats = (key) => {
    const values = chartData.map(point => point[key]).filter(val => val != null);
    if (values.length === 0) return { min: 0, max: 0, avg: 0, latest: 0 };

    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    const latest = values[values.length - 1];

    return { min, max, avg, latest };
  };

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedProfile} onChange={handleProfileChange}>
          {profiles.map((profile) => (
            <Tab
              key={profile.index}
              label={profile.name}
              value={profile.index}
            />
          ))}
        </Tabs>
      </Box>

      {/* Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            BME688 - {profiles[selectedProfile].name} Over Time
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Showing {chartData.length} data points • Metrics: {measurementKeys.map(formatMeasurementName).join(', ')}
          </Typography>

          <Box sx={{ width: '100%', height: 500 }}>
            <LineChart
              xAxis={[
                {
                  data: xAxisData,
                  scaleType: 'time',
                  valueFormatter: (value) => {
                    const date = new Date(value);
                    return date.toLocaleTimeString('cs-CZ', {
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  },
                }
              ]}
              series={series}
              width={undefined}
              height={450}
              margin={{ left: 80, right: 80, top: 20, bottom: 80 }}
              sx={{
                [`.${axisClasses.left} .${axisClasses.label}`]: {
                  transform: 'translate(-10px, 0)',
                },
                [`.${axisClasses.bottom} .${axisClasses.label}`]: {
                  transform: 'translate(0, 10px)',
                },
              }}
              slotProps={{
                legend: {
                  direction: 'row',
                  position: { vertical: 'bottom', horizontal: 'middle' },
                  padding: 0,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Statistics Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Summary Statistics - {profiles[selectedProfile].name}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Measurement</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Min</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Max</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Average</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Latest</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {measurementKeys.map((key, index) => {
                  const stats = calculateStats(key);
                  const unit = getUnit(key);
                  const color = colors[index % colors.length];

                  return (
                    <TableRow key={key}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: color,
                              mr: 1,
                            }}
                          />
                          {formatMeasurementName(key)} ({unit})
                        </Box>
                      </TableCell>
                      <TableCell>
                        {stats.min.toFixed(1)} {unit}
                      </TableCell>
                      <TableCell>
                        {stats.max.toFixed(1)} {unit}
                      </TableCell>
                      <TableCell>
                        {stats.avg.toFixed(1)} {unit}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {stats.latest.toFixed(1)} {unit}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
}

// Component for regular sensors
function RegularSensorChart({ measurements, sensorName }) {
  // Process the data for the chart
  const chartData = useMemo(() => {
    return measurements
      .map(item => {
        const timestamp = new Date(item.timestamp);
        const sensorData = item.measurement_data?.[sensorName];

        if (!sensorData || typeof sensorData !== 'object') return null;

        return {
          timestamp: timestamp.getTime(),
          timestampFormatted: timestamp.toLocaleString(),
          ...sensorData
        };
      })
      .filter(item => item !== null)
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [measurements, sensorName]);

  if (chartData.length === 0) {
    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Alert severity="warning">
            No data available for sensor "{sensorName}" in the measurements.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Get the sensor data keys (co2, hum, temp, etc.)
  const sampleData = chartData[0];
  const measurementKeys = Object.keys(sampleData).filter(key =>
    key !== 'timestamp' && key !== 'timestampFormatted' && typeof sampleData[key] === 'number'
  );

  const colors = getLineColors();

  // Prepare data for MUI X Charts
  const xAxisData = chartData.map(point => point.timestamp);

  const series = measurementKeys.map((key, index) => ({
    id: key,
    label: `${formatMeasurementName(key)} (${getUnit(key)})`,
    color: colors[index % colors.length],
    data: chartData.map(point => point[key]),
    connectNulls: true
  }));

  // Calculate statistics for each measurement
  const calculateStats = (key) => {
    const values = chartData.map(point => point[key]).filter(val => val != null);
    if (values.length === 0) return { min: 0, max: 0, avg: 0, latest: 0 };

    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    const latest = values[values.length - 1];

    return { min, max, avg, latest };
  };

  return (
    <>
      {/* Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {sensorName.toUpperCase()} Sensor Data Over Time
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Showing {chartData.length} data points • Metrics: {measurementKeys.map(formatMeasurementName).join(', ')}
          </Typography>

          <Box sx={{ width: '100%', height: 500 }}>
            <LineChart
              xAxis={[
                {
                  data: xAxisData,
                  scaleType: 'time',
                  valueFormatter: (value) => {
                    const date = new Date(value);
                    return date.toLocaleTimeString('cs-CZ', {
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  },
                }
              ]}
              series={series}
              width={undefined}
              height={450}
              margin={{ left: 80, right: 80, top: 20, bottom: 80 }}
              sx={{
                [`.${axisClasses.left} .${axisClasses.label}`]: {
                  transform: 'translate(-10px, 0)',
                },
                [`.${axisClasses.bottom} .${axisClasses.label}`]: {
                  transform: 'translate(0, 10px)',
                },
              }}
              slotProps={{
                legend: {
                  direction: 'row',
                  position: { vertical: 'bottom', horizontal: 'middle' },
                  padding: 0,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Statistics Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Summary Statistics
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Measurement</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Min</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Max</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Average</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Latest</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {measurementKeys.map((key, index) => {
                  const stats = calculateStats(key);
                  const unit = getUnit(key);
                  const color = colors[index % colors.length];

                  return (
                    <TableRow key={key}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: color,
                              mr: 1,
                            }}
                          />
                          {formatMeasurementName(key)} ({unit})
                        </Box>
                      </TableCell>
                      <TableCell>
                        {stats.min.toFixed(1)} {unit}
                      </TableCell>
                      <TableCell>
                        {stats.max.toFixed(1)} {unit}
                      </TableCell>
                      <TableCell>
                        {stats.avg.toFixed(1)} {unit}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {stats.latest.toFixed(1)} {unit}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
}

export default function SensorCompositeGraph({ boxId, sensorName, onBack }) {
  console.log('🔧 SensorCompositeGraph props:', { boxId, sensorName });

  // Fetch fresh data using the hook - NOTE: Changed from 'data' to 'measurements'
  const { measurements, loading, error } = useSensorMeasurements(boxId, sensorName);

  console.log('📊 SensorCompositeGraph hook data:', {
    measurements,
    loading,
    error,
    measurementsType: typeof measurements,
    measurementsIsArray: Array.isArray(measurements)
  });

  // Determine if this is BME688 sensor
  const isBME688 = sensorName === 'bme688';

  // Handle loading state
  if (loading) {
    return (
      <Box sx={{
        width: '100%',
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading {sensorName} data...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Box sx={{
        width: '100%',
        minHeight: '100vh',
        bgcolor: 'grey.50',
        p: 3
      }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography component="h1" variant="h4" sx={{ fontWeight: 600 }}>
                Composite Graph - {sensorName}
              </Typography>
            </Box>
            <Alert severity="error">
              Error loading sensor data: {error.message || 'Unknown error'}
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Handle empty or null data
  if (!measurements || !Array.isArray(measurements) || measurements.length === 0) {
    return (
      <Box sx={{
        width: '100%',
        minHeight: '100vh',
        bgcolor: 'grey.50',
        p: 3
      }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography component="h1" variant="h4" sx={{ fontWeight: 600 }}>
                Composite Graph - {sensorName}
              </Typography>
            </Box>
            <Alert severity="info">
              No measurement data available for sensor "{sensorName}" in box "{boxId}".
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      bgcolor: 'grey.50',
      p: 3
    }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography component="h1" variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  Composite Graph - {sensorName.toUpperCase()}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Box ID: {boxId} • {measurements.length} measurements
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Chart and Statistics */}
      {isBME688 ? (
        <BME688CompositeChart measurements={measurements} />
      ) : (
        <RegularSensorChart
          measurements={measurements}
          sensorName={sensorName}
        />
      )}
    </Box>
  );
}